import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, useTheme } from '@mui/material';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { productPhotos } from '@/utils/propertyEnrichment';
import { Product } from '@/types/ProductType';
import { CommonImage } from '../Common/CommonImage';

type ProductDetailLogos = {
  product: Product;
};

export const ProductDetailLogos = ({ product }: ProductDetailLogos) => {
  const theme = useTheme();
  const [currentMainImageIndex, setCurrentMainImageIndex] = useState(0);

  if (!product) return null;

  // Create combined array with main image first, then additional photos
  const photos = productPhotos(product);
  const allImages = photos.length ? photos : [''];

  // Only enable loop if there are enough slides (more than 1)
  const shouldLoop = allImages.length > 1;

  const handlePreviousImage = () => {
    setCurrentMainImageIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentMainImageIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentMainImageIndex(index);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          aspectRatio: '4 / 3',
          borderRadius: '12px',
          borderStyle: 'solid',
          borderWidth: '2px',
          borderColor: theme.palette.primary.main,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <CommonImage photo={allImages[currentMainImageIndex]} />

        {/* Navigation arrows - only show if there are multiple images */}
        {allImages.length > 1 && (
          <>
            <IconButton
              onClick={handlePreviousImage}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                },
                zIndex: 2,
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                },
                zIndex: 2,
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}

        {/* Thumbnail carousel superimposed at the bottom */}
        {allImages.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 3,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              borderRadius: '20px',
              px: 2,
              py: 1,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Swiper
              slidesPerView="auto"
              slidesPerGroup={1}
              loop={shouldLoop}
              spaceBetween={8}
              modules={[Navigation, Pagination]}
              style={{ width: 'auto', maxWidth: '300px' }}
            >
              {allImages.map((photo, idx) => (
                <SwiperSlide
                  key={idx}
                  style={{ width: '60px', height: '45px' }}
                >
                  <Box
                    onClick={() => handleThumbnailClick(idx)}
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '6px',
                      border: '2px solid',
                      borderColor:
                        idx === currentMainImageIndex
                          ? theme.palette.primary.main
                          : 'rgba(255, 255, 255, 0.3)',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <CommonImage photo={photo} />
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        )}
      </Box>
    </Box>
  );
};
